
// Centralized toast system using sonner
import { toast as sonnerToast } from "sonner";
import type { ToastT } from "sonner";

// Define an interface for our toast object parameters
interface ToastOptions {
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive';
  duration?: number;
}

// Create a callable function that also has properties
type ToastFunction = {
  (message: string | ToastOptions): string | number;
  error: (message: string | ToastOptions) => string | number;
  success: (message: string | ToastOptions) => string | number;
  warning: (message: string | ToastOptions) => string | number;
  info: (message: string | ToastOptions) => string | number;
  promise: typeof sonnerToast.promise;
  dismiss: typeof sonnerToast.dismiss;
  custom: typeof sonnerToast.custom;
  message: typeof sonnerToast.message;
  loading: typeof sonnerToast.loading;
  getHistory: typeof sonnerToast.getHistory;
};

// Helper to normalize toast parameters
const normalizeToastParams = (message: string | ToastOptions) => {
  if (typeof message === 'string') {
    return { title: message };
  }
  return message;
};

// Create a callable function that can be invoked directly or via methods
export const toast = (function createToastFunction() {
  const toastFn = ((message: string | ToastOptions) => {
    const params = normalizeToastParams(message);
    return sonnerToast(params.title || '', {
      description: params.description,
      duration: params.duration
    });
  }) as ToastFunction;
  
  // Add method properties to the function
  toastFn.error = (message: string | ToastOptions) => {
    const params = normalizeToastParams(message);
    return sonnerToast.error(params.title || '', {
      description: params.description,
      duration: params.duration
    });
  };
  
  toastFn.success = (message: string | ToastOptions) => {
    const params = normalizeToastParams(message);
    return sonnerToast.success(params.title || '', {
      description: params.description,
      duration: params.duration
    });
  };
  
  toastFn.warning = (message: string | ToastOptions) => {
    const params = normalizeToastParams(message);
    return sonnerToast.warning(params.title || '', {
      description: params.description,
      duration: params.duration
    });
  };
  
  toastFn.info = (message: string | ToastOptions) => {
    const params = normalizeToastParams(message);
    return sonnerToast.info(params.title || '', {
      description: params.description,
      duration: params.duration
    });
  };
  
  // Pass through other methods directly
  toastFn.promise = sonnerToast.promise;
  toastFn.dismiss = sonnerToast.dismiss;
  toastFn.custom = sonnerToast.custom;
  toastFn.message = sonnerToast.message;
  toastFn.loading = sonnerToast.loading;
  toastFn.getHistory = sonnerToast.getHistory;
  
  return toastFn;
})();

// Mock the old useToast hook API for backward compatibility
export function useToast() {
  return {
    toast,
    toasts: [],
    dismiss: toast.dismiss,
  };
}
